import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  uploadUserPicture,
  deleteUserPicture,
  fetchUserPicture,
} from "../store/thunks/userThunk";
import { XMarkIcon, PhotoIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PROFILE_PLACEHOLDER } from "../utils/placeholders";

function ProfilePictureModal({ userId, onClose }) {
  const dispatch = useDispatch();
  const { profilePicture, loading, error } = useSelector(
    (state) => state.users
  );

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(profilePicture);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  const canvasRef = useRef();
  const imgRef = useRef();
  const fileInputRef = useRef();

  useEffect(() => {
    if (!selectedFile) {
      setPreview(profilePicture || PROFILE_PLACEHOLDER);
    }
  }, [profilePicture, selectedFile]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = imgRef.current;

    const imgWidth = img.width * zoom;
    const imgHeight = img.height * zoom;

    canvas.width = 300;
    canvas.height = 300;

    const offsetX = (canvas.width - imgWidth) / 2;
    const offsetY = (canvas.height - imgHeight) / 2;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      img,
      position.x + offsetX,
      position.y + offsetY,
      imgWidth,
      imgHeight
    );
  }, [position, zoom]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setLastPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const dx = e.clientX - lastPos.x;
    const dy = e.clientY - lastPos.y;

    setPosition((pos) => ({ x: pos.x + dx, y: pos.y + dy }));
    setLastPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleUpload = async () => {
    const canvas = canvasRef.current;

    canvas.toBlob(async (blob) => {
      const formData = new FormData();
      formData.append("profilePicture", blob, "profile.jpg");

      await dispatch(uploadUserPicture({ userId, formData }));
      dispatch(fetchUserPicture({ userId }));
      onClose();
    }, "image/jpeg");
  };

  const handleDelete = async () => {
    await dispatch(deleteUserPicture({ userId }));
    dispatch(fetchUserPicture({ userId }));
    onClose();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseup", handleMouseUp);

    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-sm bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Edit Profile Picture</h2>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="relative mb-6">
          <div className="relative mx-auto h-72 w-72 overflow-hidden rounded-full ring-1 ring-gray-200">
            <img
              ref={imgRef}
              src={preview}
              alt="Profile Preview"
              style={{ display: "none" }}
              onLoad={drawCanvas}
            />
            <canvas
              ref={canvasRef}
              className="h-full w-full"
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            />
          </div>
        </div>

        <div className="mb-6 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Zoom</label>
            <input
              type="range"
              min={0.25}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              onInput={drawCanvas}
              className="h-2 w-full rounded-sm bg-gray-200 accent-indigo-600"
            />
          </div>

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-sm border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
              <PhotoIcon className="h-5 w-5 text-gray-400" />
              Choose Image
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-sm bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 rounded-sm border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
            disabled={loading}>
            <TrashIcon className="h-4 w-4" />
            Delete
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="rounded-sm border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
              Cancel
            </button>
            <button
              onClick={handleUpload}
              className="rounded-sm bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              disabled={loading}>
              {loading ? "Uploading..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePictureModal;
